import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Stethoscope, LogOut, Inbox, FileCheck2, UserCog, ArrowLeft, FileText,
  ShieldAlert, ClipboardList, Send, Clock, Star,
} from "lucide-react";
import spApi, { spErrorMessage, getSpToken, setSpToken } from "@/lib/spApi";

const card = "bg-[#1A1A1A] border border-[#333333] rounded-2xl";
const input = "w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3 min-h-[44px] text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27]";
const label = "block text-sm font-medium text-[#A1A1AA] mb-1.5 mt-3";

function Snapshot({ snap }) {
  const rows = [
    ["Conditions", snap?.conditions],
    ["Medications", snap?.medications],
    ["Allergies", snap?.allergies],
  ];
  return (
    <div className={`${card} p-4 mt-3`} data-testid="sp-case-snapshot">
      <p className="text-xs font-bold tracking-wide text-[#A1A1AA] mb-2">PATIENT HEALTH SNAPSHOT</p>
      {rows.map(([k, v]) => (
        <p key={k} className="text-sm text-[#E5E5E5]">
          <span className="text-[#A1A1AA]">{k}:</span> {v?.length ? v.join(", ") : "None recorded"}
        </p>
      ))}
    </div>
  );
}

function OpinionForm({ caseId, onDone }) {
  const [summary, setSummary] = useState("");
  const [considerations, setConsiderations] = useState("");
  const [questions, setQuestions] = useState("");
  const [nextSteps, setNextSteps] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!summary.trim()) return toast.error("A summary is required.");
    setBusy(true);
    try {
      await spApi.post(`/specialists/cases/${caseId}/opinion`, {
        summary,
        considerations: considerations.split("\n").map((s) => s.trim()).filter(Boolean),
        suggested_questions: questions.split("\n").map((s) => s.trim()).filter(Boolean),
        next_steps: nextSteps,
      });
      toast.success("Opinion submitted. The patient has been notified.");
      onDone();
    } catch (e) {
      toast.error(spErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${card} p-4 mt-4`} data-testid="sp-opinion-form">
      <h3 className="font-head font-bold text-[#F8FAFC] flex items-center gap-2">
        <ClipboardList size={18} className="text-[#EF9F27]" /> Write your second opinion
      </h3>
      <p className="text-xs text-[#A1A1AA] mt-1">Provide guidance to discuss with the patient's treating doctor. Do not prescribe specific drugs or dosages.</p>
      <label className={label}>Clinical summary</label>
      <textarea data-testid="sp-op-summary" rows={4} className={input} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Your assessment of the concern based on the information provided." />
      <label className={label}>Key considerations (one per line)</label>
      <textarea data-testid="sp-op-considerations" rows={4} className={input} value={considerations} onChange={(e) => setConsiderations(e.target.value)} placeholder={"e.g. History is consistent with…\nBring original imaging to any appointment"} />
      <label className={label}>Questions to ask the local doctor (one per line)</label>
      <textarea data-testid="sp-op-questions" rows={3} className={input} value={questions} onChange={(e) => setQuestions(e.target.value)} placeholder={"e.g. Are further tests warranted?"} />
      <label className={label}>Suggested next steps</label>
      <textarea data-testid="sp-op-next" rows={3} className={input} value={nextSteps} onChange={(e) => setNextSteps(e.target.value)} placeholder="What the patient should do next." />
      <button data-testid="sp-op-submit" onClick={submit} disabled={busy}
        className="mt-4 w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-4 py-3 min-h-[48px] disabled:opacity-60 flex items-center justify-center gap-2">
        <Send size={16} /> {busy ? "Submitting…" : "Submit opinion"}
      </button>
    </div>
  );
}

function CaseDetail({ caseId, onBack, readonly }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    spApi.get(`/specialists/cases/${caseId}`).then(({ data }) => setC(data)).catch(() => {}).finally(() => setLoading(false));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className={`${card} h-40 animate-pulse mt-4`} />;
  if (!c) return null;
  const answered = c.opinion_source === "specialist";

  return (
    <div data-testid="sp-case-detail">
      <button data-testid="sp-case-back" onClick={onBack} className="flex items-center gap-1 text-sm text-[#A1A1AA] mb-3 min-h-[44px]">
        <ArrowLeft size={16} /> Back
      </button>
      <div className={`${card} p-4`}>
        <p className="text-xs text-[#EF9F27] font-bold">{c.patient_ref} · {c.specialty}</p>
        <h2 className="font-head text-lg font-bold text-[#F8FAFC] mt-1">{c.title}</h2>
        <p className="text-sm text-[#E5E5E5] mt-2 leading-relaxed whitespace-pre-wrap">{c.concern}</p>
      </div>
      <Snapshot snap={c.profile_snapshot} />
      {c.documents?.length > 0 && (
        <div className={`${card} p-4 mt-3`}>
          <p className="text-xs font-bold tracking-wide text-[#A1A1AA] mb-2">ATTACHED RECORDS</p>
          <ul className="space-y-1">
            {c.documents.map((d) => (
              <li key={d.id} className="text-sm text-[#E5E5E5] flex items-center gap-2">
                <FileText size={14} className="text-[#EF9F27]" /> {d.label || "Record"} · {d.category}
              </li>
            ))}
          </ul>
        </div>
      )}
      {answered ? (
        <div className={`${card} p-4 mt-4`} data-testid="sp-case-answered">
          <p className="text-xs font-bold tracking-wide text-[#22C55E] mb-2">YOUR SUBMITTED OPINION</p>
          <p className="text-sm text-[#E5E5E5] leading-relaxed">{c.opinion?.summary}</p>
          {c.opinion?.considerations?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {c.opinion.considerations.map((x, i) => (
                <li key={i} className="text-sm text-[#E5E5E5] flex gap-2"><span className="text-[#EF9F27]">•</span> {x}</li>
              ))}
            </ul>
          )}
        </div>
      ) : readonly ? null : (
        <OpinionForm caseId={c.id} onDone={onBack} />
      )}
    </div>
  );
}

function Bookings() {
  const [cases, setCases] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    spApi.get("/specialists/cases").then(({ data }) => setCases(data)).catch(() => setCases([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (open) return <CaseDetail caseId={open} onBack={() => { setOpen(null); load(); }} />;
  if (cases === null) return <div className={`${card} h-24 animate-pulse`} />;
  if (cases.length === 0)
    return (
      <div className={`${card} p-6 text-center`} data-testid="sp-bookings-empty">
        <Inbox size={28} className="text-[#EF9F27] mx-auto" />
        <p className="text-[#F8FAFC] font-bold mt-2">No cases in your queue</p>
        <p className="text-sm text-[#A1A1AA] mt-1">New {""}patient concerns matching your specialty will appear here.</p>
      </div>
    );
  return (
    <div className="space-y-3" data-testid="sp-bookings-list">
      {cases.map((c) => (
        <button key={c.id} data-testid={`sp-case-${c.id}`} onClick={() => setOpen(c.id)}
          className={`${card} p-4 w-full text-left hover:border-[#EF9F27] transition-colors`}>
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#EF9F27] font-bold">{c.patient_ref}</p>
            <span className="text-xs text-[#A1A1AA] flex items-center gap-1"><Clock size={12} /> {(c.submitted_at || "").slice(0, 10)}</span>
          </div>
          <p className="font-head font-bold text-[#F8FAFC] mt-1">{c.title}</p>
          <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-2">{c.concern}</p>
        </button>
      ))}
    </div>
  );
}

function Reports() {
  const [cases, setCases] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    spApi.get("/specialists/reports").then(({ data }) => setCases(data)).catch(() => setCases([]));
  }, []);
  if (open) return <CaseDetail caseId={open} onBack={() => setOpen(null)} readonly />;
  if (cases === null) return <div className={`${card} h-24 animate-pulse`} />;
  if (cases.length === 0)
    return (
      <div className={`${card} p-6 text-center`} data-testid="sp-reports-empty">
        <FileCheck2 size={28} className="text-[#EF9F27] mx-auto" />
        <p className="text-[#F8FAFC] font-bold mt-2">No submitted opinions yet</p>
        <p className="text-sm text-[#A1A1AA] mt-1">Opinions you submit will be listed here.</p>
      </div>
    );
  return (
    <div className="space-y-3" data-testid="sp-reports-list">
      {cases.map((c) => (
        <button key={c.id} data-testid={`sp-report-${c.id}`} onClick={() => setOpen(c.id)}
          className={`${card} p-4 w-full text-left hover:border-[#EF9F27] transition-colors`}>
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#EF9F27] font-bold">{c.patient_ref}</p>
            <span className="text-xs text-[#22C55E]">Submitted {(c.answered_at || "").slice(0, 10)}</span>
          </div>
          <p className="font-head font-bold text-[#F8FAFC] mt-1">{c.title}</p>
          <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-2">{c.opinion?.summary}</p>
        </button>
      ))}
    </div>
  );
}

function Profile({ sp, onUpdated }) {
  const [f, setF] = useState({
    credentials_summary: sp.credentials_summary || "",
    review_fee: sp.review_fee ?? "",
    currency: sp.currency || "USD",
    avg_turnaround_days: sp.avg_turnaround_days ?? 3,
  });
  const [busy, setBusy] = useState(false);
  const [earn, setEarn] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    spApi.get("/specialists/earnings").then(({ data }) => setEarn(data)).catch(() => {});
  }, [sp]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await spApi.put("/specialists/me", {
        credentials_summary: f.credentials_summary,
        review_fee: Number(f.review_fee) || 0,
        currency: f.currency,
        avg_turnaround_days: Number(f.avg_turnaround_days) || 3,
      });
      onUpdated(data);
      toast.success("Profile updated.");
    } catch (e) {
      toast.error(spErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="sp-profile">
      <div className={`${card} p-4`}>
        <p className="font-head font-bold text-[#F8FAFC]">{sp.full_name}</p>
        <p className="text-sm text-[#EF9F27]">{sp.specialty}</p>
        <p className="text-xs text-[#A1A1AA] mt-1">{sp.country} · Languages: {(sp.languages || []).join(", ") || "—"}</p>
        <div className="mt-3 pt-3 border-t border-[#333333] text-sm text-[#E5E5E5] space-y-1">
          <p><span className="text-[#A1A1AA]">Licence:</span> {sp.licence_number} · {sp.licence_body}</p>
          <p><span className="text-[#A1A1AA]">Expiry:</span> {sp.licence_no_expiry ? "No expiry" : sp.licence_expiry_date}</p>
          <p><span className="text-[#A1A1AA]">Status:</span> <span className="text-[#22C55E] font-semibold">Verified</span></p>
        </div>
      </div>

      {/* Earnings & rating summary */}
      <div className={`${card} p-4 mt-3`} data-testid="sp-earnings">
        <p className="text-xs font-bold tracking-wide text-[#A1A1AA] mb-3">EARNINGS & RATING</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="font-head text-2xl font-extrabold text-[#EF9F27]" data-testid="sp-earn-completed">{earn?.completed ?? "—"}</p>
            <p className="text-xs text-[#A1A1AA] mt-1">Reviews done</p>
          </div>
          <div>
            <p className="font-head text-2xl font-extrabold text-[#22C55E]" data-testid="sp-earn-total">
              {earn ? `${earn.total}` : "—"}
            </p>
            <p className="text-xs text-[#A1A1AA] mt-1">{earn?.currency || ""} earned</p>
          </div>
          <div>
            <p className="font-head text-2xl font-extrabold text-[#F8FAFC] flex items-center justify-center gap-1" data-testid="sp-earn-rating">
              {earn?.rating_avg != null ? (<><Star size={16} className="text-[#EF9F27] fill-[#EF9F27]" />{earn.rating_avg}</>) : "—"}
            </p>
            <p className="text-xs text-[#A1A1AA] mt-1">{earn?.rating_count ? `${earn.rating_count} rating(s)` : "No ratings yet"}</p>
          </div>
        </div>
        <p className="text-xs text-[#666666] mt-3 text-center">Earnings are indicative, based on your set fee × completed reviews.</p>
      </div>

      <div className={`${card} p-4 mt-3`}>
        <h3 className="font-head font-bold text-[#F8FAFC] mb-1">Editable details</h3>
        <label className={label}>Credentials summary</label>
        <textarea data-testid="sp-profile-credentials" rows={3} className={input} value={f.credentials_summary} onChange={(e) => set("credentials_summary", e.target.value)} />
        <label className={label}>Review fee</label>
        <div className="flex gap-2">
          <input data-testid="sp-profile-fee" type="number" className={input} value={f.review_fee} onChange={(e) => set("review_fee", e.target.value)} />
          <select data-testid="sp-profile-currency" className={`${input} w-28`} value={f.currency} onChange={(e) => set("currency", e.target.value)}>
            {["USD", "GBP", "EUR", "GHS", "NGN", "KES", "ZAR"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <label className={label}>Average turnaround (business days)</label>
        <input data-testid="sp-profile-turnaround" type="number" min={1} className={input} value={f.avg_turnaround_days} onChange={(e) => set("avg_turnaround_days", e.target.value)} />
        <button data-testid="sp-profile-save" onClick={save} disabled={busy}
          className="mt-4 w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-4 py-3 min-h-[48px] disabled:opacity-60">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

const TABS = [
  { key: "bookings", label: "Bookings", icon: Inbox },
  { key: "reports", label: "Reports", icon: FileCheck2 },
  { key: "profile", label: "Profile", icon: UserCog },
];

export default function SpecialistPortal() {
  const navigate = useNavigate();
  const [sp, setSp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!getSpToken()) { navigate("/specialist/login", { replace: true }); return; }
    spApi.get("/specialists/me")
      .then(({ data }) => {
        if (data.verification_status !== "verified") {
          navigate("/specialist/status", { replace: true });
          return;
        }
        setSp(data);
        spApi.get("/specialists/notifications").then(({ data: n }) => setUnread(n.unread || 0)).catch(() => {});
      })
      .catch(() => navigate("/specialist/login", { replace: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  const selectTab = (key) => {
    setTab(key);
    if (key === "bookings" && unread > 0) {
      setUnread(0);
      spApi.post("/specialists/notifications/read").catch(() => {});
    }
  };

  const logout = () => { setSpToken(null); navigate("/specialist/login", { replace: true }); };

  if (loading || !sp) {
    return (
      <div className="care-scope min-h-screen w-full flex justify-center bg-black">
        <div className="w-full max-w-[430px] min-h-screen bg-[#111111] p-6">
          <div className="h-40 bg-[#1A1A1A] border border-[#333333] rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="care-scope min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-[430px] min-h-screen bg-[#111111] flex flex-col">
        <header className="px-6 pt-8 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#EF9F27] text-[#111111]">
              <Stethoscope size={20} />
            </div>
            <div>
              <p className="font-head font-extrabold text-[#F8FAFC] leading-tight">Specialist portal</p>
              <p className="text-xs text-[#A1A1AA]">{sp.full_name}</p>
            </div>
          </div>
          <button data-testid="sp-portal-logout" onClick={logout} className="text-[#A1A1AA] flex items-center gap-1 text-sm min-h-[44px]">
            <LogOut size={16} />
          </button>
        </header>

        <div className="px-6 pb-2 flex items-start gap-2 text-xs text-[#EFC77A] bg-[#EF9F27]/5">
          <ShieldAlert size={14} className="shrink-0 mt-0.5" />
          <span>Patient identities are hidden. Provide guidance only — never prescribe specific drugs or dosages.</span>
        </div>

        <div className="flex-1 px-6 py-4 pb-28">
          {tab === "bookings" && <Bookings />}
          {tab === "reports" && <Reports />}
          {tab === "profile" && <Profile sp={sp} onUpdated={setSp} />}
        </div>

        <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] bg-[#0A0A0A] border-t border-[#333333] flex">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`sp-tab-${t.key}`} onClick={() => selectTab(t.key)}
                className={`relative flex-1 flex flex-col items-center gap-1 py-3 min-h-[56px] ${active ? "text-[#EF9F27]" : "text-[#A1A1AA]"}`}>
                <Icon size={20} />
                {t.key === "bookings" && unread > 0 && (
                  <span data-testid="sp-bookings-badge" className="absolute top-2 right-1/2 translate-x-4 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF9F27] text-[#111111] text-[10px] font-bold flex items-center justify-center">
                    {unread}
                  </span>
                )}
                <span className="text-xs font-semibold">{t.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
